/**
 * Numbers, money, dates and the two grammatical helpers, as vB6 formats them.
 *
 * Handover §5.12 *Number and date formats*, DR-12 (`build/FIXES.md` item 15)
 * for the two English date formats and DR-20 (item 23) for the Hungarian
 * article. Every rule below is vB6's; what changed in the port is that the
 * language is an argument rather than a global.
 */

import type { Lang } from './lang.ts'

const NBSP = ' '
const MINUS = '−'

/**
 * HU: counts follow AkH. 12 — four digits together (1200), five and more in
 * groups of three; forint amounts are grouped from four digits (4 118 Ft);
 * decimal comma (8,8); minus sign U+2212.
 * EN: comma groups from four digits (4,118); decimal point (8.8).
 */
export function nf(lang: Lang, value: number, decimals = 0, groupFromFour = false): string {
  const negative = value < 0
  const [whole, fraction] = Math.abs(Number(value)).toFixed(decimals).split('.')
  const hu = lang === 'hu'
  const grouped =
    hu && (whole ?? '').length <= 4 && !groupFromFour
      ? (whole ?? '')
      : (whole ?? '').replace(/\B(?=(\d{3})+(?!\d))/g, hu ? NBSP : ',')
  return (negative ? MINUS : '') + grouped + (fraction ? (hu ? ',' : '.') + fraction : '')
}

/**
 * Forint amounts are grouped from four digits as well (3 900 Ft), as in price
 * lists and invoices, so amounts in a column always read alike.
 */
export const ft = (lang: Lang, value: number): string =>
  lang === 'hu'
    ? nf(lang, Math.round(value), 0, true) + NBSP + 'Ft'
    : 'HUF' + NBSP + nf(lang, Math.round(value))

export const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

const WEEKDAYS = {
  hu: ['vasárnap', 'hétfő', 'kedd', 'szerda', 'csütörtök', 'péntek', 'szombat'],
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
} as const

const MONTHS_HU = [
  'január', 'február', 'március', 'április', 'május', 'június',
  'július', 'augusztus', 'szeptember', 'október', 'november', 'december',
] as const

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const

/**
 * DR-12 (`build/FIXES.md` item 15), options C and D. §5.2 gives one date
 * format and it is Hungarian; the English the prototype arrived with was
 * 07/10/2026 with a bare weekday after it, a shape no English convention
 * uses. Barna set both: the long date *Tuesday, 20 October 2026* and the
 * short date *Tue 20 Oct 2026* at every call site.
 */
export function dt(lang: Lang, d: Date, withWeekday = false): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  if (lang === 'hu') {
    return withWeekday
      ? `${year}. ${month}. ${day}. ${WEEKDAYS.hu[d.getDay()]}`
      : `${year}. ${month}. ${day}.`
  }
  const short = `${d.getDate()} ${(MONTHS_EN[d.getMonth()] ?? '').slice(0, 3)} ${year}`
  return withWeekday ? `${(WEEKDAYS.en[d.getDay()] ?? '').slice(0, 3)} ${short}` : short
}

/**
 * Run 11 item 5c: an instant as Budapest shows it — the calendar day (as a
 * local Date at midday, which `dt` formats) and the clock, `HH:MM`. The
 * payment deadline is an instant the database worked out in Budapest time,
 * and the visitor's browser may be anywhere.
 */
export function budapestWallClock(d: Date): { day: Date; hhmm: string } {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Budapest',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(d)
      .map((part) => [part.type, part.value]),
  )
  return {
    day: new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day), 12),
    hhmm: `${parts.hour}:${parts.minute}`,
  }
}

export const dtLong = (lang: Lang, d: Date): string =>
  lang === 'hu'
    ? `${d.getFullYear()}. ${MONTHS_HU[d.getMonth()]} ${d.getDate()}., ${WEEKDAYS.hu[d.getDay()]}`
    : `${WEEKDAYS.en[d.getDay()]}, ${d.getDate()} ${MONTHS_EN[d.getMonth()]} ${d.getFullYear()}`

/**
 * DR-20 (`build/FIXES.md` item 23), option D. Hungarian chooses *a* or *az*
 * by the sound the next word starts with, and the two §5.3 strings put a
 * number there. Over 0–400, the whole range the page can produce, the
 * numerals whose first word begins with a vowel are 1 (*egy*), 5 (*öt*) and
 * 50–59 (*ötven…*); every other first word — *nulla*, *kettő*, *három*, …,
 * *száz*, *négyszáz* — begins with a consonant, and a numeral's article
 * follows its first word (*százöt* is *a százöt*, not *az*).
 */
export function huArt(n: number, capitalised = false): string {
  const v = Math.abs(Math.round(Number(n) || 0))
  const article = v === 1 || v === 5 || (v >= 50 && v <= 59) ? 'az' : 'a'
  return capitalised ? article.charAt(0).toUpperCase() + article.slice(1) : article
}

/**
 * The English counterpart of `huArt`: a count interpolated into an English
 * sentence has to agree with its noun, where Hungarian leaves the noun
 * singular after a numeral and needs nothing.
 */
export const enPl = (n: number, one: string, many: string): string =>
  Math.abs(Math.round(Number(n) || 0)) === 1 ? one : many

/** DR-13 (`build/FIXES.md` item 16): each blocker is its own sentence. */
export function sentence(s: string): string {
  const trimmed = String(s).trim()
  const capitalised = trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
  return /[.!?]$/.test(capitalised) ? capitalised : capitalised + '.'
}

/** §5.9: "+36… or an international number." The same rule the server applies. */
export function phoneOk(value: string): boolean {
  const bare = value.replace(/[\s\-()]/g, '')
  return bare.startsWith('+36') ? /^\+36\d{8,9}$/.test(bare) : /^\+\d{7,15}$/.test(bare)
}

/** §5.10: *Adószám*, as the page and the server both validate it. */
export const vatOk = (value: string): boolean => /^\d{8}-\d-\d{2}$/.test(value)

export const emailOk = (value: string): boolean => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)

export const postcodeOk = (value: string): boolean => /^\d{4}$/.test(value)

/**
 * A formatter bound to one language, so a component is handed the language
 * once instead of threading it through every call.
 *
 * `build/FIXES.md` item 28 — escape every interpolation of customer input —
 * has no counterpart here and that is the point of the port: React escapes
 * text by construction, and nothing in `apps/web` builds an element out of a
 * template literal. `apps/web/e2e/fixes.spec.ts` sends the same
 * `<b>x</b>@example.hu` that the prototype's regression test sends and
 * checks it comes back as text.
 */
export interface Format {
  lang: Lang
  n(value: number, decimals?: number, groupFromFour?: boolean): string
  money(value: number): string
  date(d: Date, withWeekday?: boolean): string
  longDate(d: Date): string
  article(n: number, capitalised?: boolean): string
  plural(n: number, one: string, many: string): string
}

export function formatter(lang: Lang): Format {
  return {
    lang,
    n: (value, decimals = 0, groupFromFour = false) => nf(lang, value, decimals, groupFromFour),
    money: (value) => ft(lang, value),
    date: (d, withWeekday = false) => dt(lang, d, withWeekday),
    longDate: (d) => dtLong(lang, d),
    article: huArt,
    plural: enPl,
  }
}
